import { IErrors } from './error';
import { IAddressInfo, SubscriptionType } from './Subscription';
import { IUserInfo } from './user';
import { UserPermissions } from '../_helpers/permissions.enum';

// Chapter type
export enum ChapterType {
  Book = 0,
  Preface = 1,
  Chat = 2,
  Story1 = 3,
  Story2 = 4,
  Story3 = 5,
  Story4 = 6,
  VisualSeries1 = 7,
  VisualSeries2 = 8,
  VisualSeries3 = 9,
  VisualSeries4 = 10,
  VisualSeries5 = 11,
  Topic1 = 12,
  Topic2 = 13,
  Topic3 = 14,
  Topic4 = 15,
  Topic5 = 16,
}

export enum PageType {
  Preface = 1,
  Story = 2,
  Poem = 3,
  Quiz = 4,
  About = 5,
  VisualSeries = 6,
  Chat = 7,
}

export enum PageStatus {
    Draft = 'Draft',
    WaitingForVerification = 'WaitingForVerification',
    Verified = 'Verified',
    Published = 'Published',
    Retired = 'Retired',
}

export enum BookStatus {
  Draft = 'Draft',
  WaitingForVerification = 'WaitingForVerification',
  Verified = 'Verified',
  Published = 'Published',
  Retired = 'Retired',
}
export enum TranslationStatus {
  Draft = 1,
  WaitingForVerification = 2,
  Verified = 3,
  Published = 4,
  Retired = 5,
}

export enum LanguageCode {
  en = 1, // English
  hi = 2, // Hindi
  af = 3, // Afrikaans
  am = 4, // Amharic
  ar = 5, // Arabic
  ay = 6, // Aymara
  be = 7, // Belarusian
  bn = 8, // Bengali
  bi = 9, // Bislama
  my = 10, // Burmese
  ny = 11, // Chichewa
  zh = 12, // Chinese / Mandarin
  dv = 13, // Dhivehi
  nl = 14, // Dutch
  dz = 15, // Dzongkha
  fj = 16, // Fijian
  tl = 17, // Filipino (Tagalog)
  fr = 18, // French
  kl = 19, // Greenlandic
  ht = 20, // Haitian Creole
  ho = 21, // Hiri Motu
  it = 22, // Italian
  kk = 23, // Kazakh
  km = 24, // Khmer
  rw = 25, // Kinyarwanda
  rn = 26, // Kirundi
  ku = 27, // Kurdish
  ky = 28, // Kyrgyz
  lo = 29, // Lao
  ms = 30, // Malay
  mi = 31, // Maori
  mh = 32, // Marshallese
  mn = 33, // Mongolian
  nr = 34, // Ndebele (Southern)
  ne = 35, // Nepali
  ps = 36, // Pashto
  pt = 37, // Portuguese
  qu = 38, // Quechua
  ro = 39, // Romanian
  ru = 40, // Russian
  sg = 41, // Sango
  st = 42, // Sesotho / Sotho
  sn = 43, // Shona
  sk = 44, // Slovak
  es = 45, // Spanish
  sw = 46, // Swahili
  ss = 47, // Swazi
  tg = 48, // Tajik
  th = 49, // Thai
  to = 50, // Tongan
  ts = 51, // Tsonga
  tn = 52, // Tswana
  ur = 53, // Urdu
  ve = 54, // Venda
  vi = 55, // Vietnamese
  xh = 56, // Xhosa
  zu = 57, // Zulu
}

// Book
export interface IBook {
  BookId: string;
  Title: string | null;
  Description: string | null;
  AuthorName: string | null;
  AuthorDOB: string | null;
  DefaultLanguage: string | null;
  Status: BookStatus;
  ThumbnailUrl: string | null;
  AudioUrl: string | null;
  CreatedAt?: string;
  UpdatedAt?: string;
  isLoading?: boolean; // for client use only side
  enabled?: boolean; // for client use only side
  index?: number;
}

// Chapter
export interface IChapter {
  ChapterId: string;
  GroupName: string | null;
  Type: ChapterType;
  Title: string | null;
  Description: string;
  ChapterStatus: number;
  Status: string;
  AccessRights: string[];
  AudioFileUrl: string | null;
  ThumbnailUrl: string | null;
  IconUrl: string | null;
  CreatedAt: string;
  UpdatedAt: string;
  isLoading?: boolean; // for client use only side
  SubChapters: IChapter[];
  Settings?: IVirtualChapter | null;
}

export interface IVirtualChapter {
  ChapterId: string;
  TotalPages: number;
  SubChapters: IVirtualSubChapter[];
}

export interface IVirtualSubChapter {
  Type: number;
  Pages: string[];
  startPageIndex: number;
  ChapterId: string;
  MappedChapterId?: string | null;
}

export interface IChapterUpdateResponse {
  Value: IChapter;
}

export interface IBookUpdateResponse {
  Value: IBook;
}

export enum DifficultyLevel {
  Easy = 'Easy',
  Medium = 'Medium',
  Hard = 'Hard',
}

export interface ISpelling {
  value: string;
  hint: string | null;
  alternates: string[];
  synonyms: string[];
  antonyms: string[];
  phoneticUS: string | null;
  phoneticUK: string | null;
  difficultyLevel: DifficultyLevel | null;
}

export interface IPageSpelling {
  PageId: string;
  Title: string;
  Spellings: string;
  UpdatedAt: string;
}
export interface IChapterSpellings {
  ChapterTitle: string;
  ChapterId: string;
  Pages: IPageSpelling[];
}
export interface PageSpellingUpdateItem {
  PageId: string;
  Spellings: string;
}
export interface BulkUpdatePageSpellingsRequest {
  Updates: PageSpellingUpdateItem[];
}
export interface PageSpellingDeleteItem {
  PageId: string;
  Spellings: null;
}
export interface BulkDeletePageSpellingsRequest {
  Updates: PageSpellingDeleteItem[];
}

export interface IPage {
    PageId: string;
    Title: string | null;
    PageType: PageType;
    Status: PageStatus;
    AudioUrl: string | null;
    Type: PageType;
    AccessRights: string[];
    CreatedAt: string;
    UpdatedAt: string;
    MobileLayout?: ILayout;
    WebLayout?: ILayout;
    TabletLayout?: ILayout;
    isLoading: boolean; // for client use only side
    chapterId: string; // for client use only it does come from server we add in reduce,
    RPAllowed: boolean;
    Spellings: string | null; // Stringified JSON
}

export interface ITranslation {
  TranslationID: string;
  Language: string;
  Text: string | null;
  Status: string;
  AudioUrl: string | null;
  CreatedAt: string;
  UpdatedAt: string;
}
// Editor Book Response
export interface IResponse<T> {
  Value: T;
}
// Editor Chapter Response
export interface IEditorChaptersResponse {
  Value: IChapter[];
}
export interface IEditorPageTranslationsResponse {
  Value: ITranslation[];
}

export interface IPagesResponse<T> {
  PageNumber: number | null;
  PageSize: number | null;
  RecordsInPage: number | null;
  TotalPages: number | null;
  TotalCount: number | null;
  Value: T[];
}

export interface IResponse<T> {
  Value: T;
  Errors: IErrors[];
}

// ---------------------------------------------------------------------------
// Book access management (roles + members)
// ---------------------------------------------------------------------------

// A role as returned by the user service, including its identifier so that it
// can be assigned to other entities.
export interface IRoleDetail {
  RoleId: number;
  RoleName: string;
  RoleType: number;
  Permissions: string[];
}

// Lightweight user projection used by the member picker.
export interface IMemberInfo {
  UserId: string;
  FullName: string | null;
  Email: string | null;
  MobileNumber: string | null;
  PictureUrl: string | null;
  Roles: string[];
  CreatedAt?: string;
  RoleGrantedAt?: Record<string, string>;
}

// The access assignment for a single book, as edited in the admin UI.
export interface IBookAccess {
  BookId: string;
  RoleIds: number[];
  MemberIds: string[];
}

// A role granted access to a book, as returned by the admin service.
//
// Permissions are the *effective* set: the role's permissions intersected with
// what the grant allows. HasEffectivePermissions is sent explicitly rather than
// inferred from an empty Permissions array, so the UI never has to guess
// whether an empty list means "no permissions" or "not loaded".
export interface IBookRoleAccess {
  RoleId: number;
  RoleName: string;
  Permissions: string[];
  HasEffectivePermissions: boolean;
}

// A member granted access to a book.
//
// Permissions is the grant itself - what this member may do on this book,
// whether or not any role of theirs carries it. That is a change in meaning
// from the per-book-role model it replaces, where a grant could only narrow
// what the member already held; here it widens.
//
// The server does not yet write this field with the new meaning, so until the
// backend lands it arrives as the old derived list. The screen treats it as the
// grant either way, which is why a member with no explicit grant simply shows
// none rather than showing something it would have to reinterpret.
export interface IBookMemberAccess {
  UserId: string;
  FullName: string | null;
  Email: string | null;
  PictureUrl: string | null;
  Permissions: string[];
  HasEffectivePermissions: boolean;
}

// How widely a book is visible.
//
// Derived, never stored - on the server from the grant rows, and on this side
// from the same thing. The three states differ only in what the grant list
// contains, so there is nothing separate to persist and nothing that can
// disagree with the grants it was saved alongside.
export enum BookVisibility {
  // No grants at all. Every reader reaches the book.
  Everyone = 0,

  // Closed, but granted to nobody: only the administrators who bypass book
  // scoping reach it. There is no role or member list to derive this from -
  // the server records it with a marker grant on a role that already bypasses
  // the check, so the grant cannot widen access, and the client carries it as
  // an explicit flag on the row.
  AdminsOnly = 1,

  // Granted to at least one role or member beyond the always-access set.
  Restricted = 2,
}

// Permissions whose holders reach every book regardless of grants, because the
// server's scope check bypasses them outright.
//
// The wildcard alone. book.access.manage was here too, which is what made
// Editor unassignable and forced the "Only Admin and Editor" option: a role
// that can hand itself any book was treated as never excluded from one.
//
// That is no longer the rule. Being able to *administer* access is not the
// same as *having* it, so Editor is now an ordinary assignable role and
// reaches a book only when the book names it. The wildcard stays, because a
// role holding every permission cannot coherently be denied one.
//
// Held as a list rather than named roles so moving the wildcard between roles
// needs no change here.
export const ALWAYS_ACCESS_PERMISSIONS: string[] = [UserPermissions.ADMIN_ALL];

// ---------------------------------------------------------------------------
// Per-book member permissions.
// ---------------------------------------------------------------------------

/**
 * How far a permission's answer reaches.
 *
 * Mirrors PermissionScope in the admin service. The two lists have to agree:
 * this one decides what the dialog offers, that one decides what the server
 * accepts, and a permission offered here but refused there would be a control
 * that fails on Apply for no visible reason.
 */
export type BookPermissionScope = 'book' | 'global' | 'administrative';

// A permission that can be granted to an individual member on a single book,
// grouped for display.
export interface IBookPermissionOption {
  value: string;
  label: string;

  /**
   * Defaults to the group's scope, and to 'book' when the group sets none.
   * Set per option only where a group is mixed - Book is, since a book's own
   * read and edit are grantable while creating and destroying books are not.
   */
  scope?: BookPermissionScope;
}

export interface IBookPermissionGroup {
  label: string;
  permissions: IBookPermissionOption[];

  /**
   * The scope every permission in this group has, unless one overrides it.
   *
   * Anything other than 'book' is shown greyed and explained rather than
   * omitted: leaving it out entirely would read as "this permission does not
   * exist" instead of "this one is not decided here".
   */
  scope?: BookPermissionScope;
}

// The permissions shown in the member permission dialog, grouped by the object
// they act on and marked with how far each one reaches.
//
// Static by design. These are object-level permissions - they answer "what may
// this person do to this book's contents", so every entry has to be something a
// book actually contains.
//
// Non-book-scoped entries are listed and disabled rather than left out. An
// absent permission reads as "no such thing"; a greyed one with a reason reads
// as "real, but not decided here" - which is what an operator hunting for
// "why can't I give them layouts" needs to be told.
//
// Only the wildcard '*' and 'book.access.manage' are genuinely absent. Neither
// is a capability an operator thinks in terms of granting: one is not a named
// permission at all, and the other is the very screen this dialog belongs to,
// so offering it would invite a member to widen their own grant.
export const BOOK_PERMISSION_GROUPS: IBookPermissionGroup[] = [
  {
    label: 'Book',
    permissions: [
      { value: UserPermissions.BOOK_READ, label: 'View book' },
      { value: UserPermissions.BOOK_UPDATE, label: 'Edit book details' },

      // The one mixed group. Both of these act on books rather than on this
      // book, so neither can be scoped to the grant they would hang from.
      {
        value: UserPermissions.BOOK_CREATE,
        label: 'Create books',
        scope: 'administrative',
      },
      {
        value: UserPermissions.BOOK_DELETE,
        label: 'Delete books',
        scope: 'administrative',
      },
    ],
  },
  {
    label: 'Chapters',
    permissions: [
      { value: UserPermissions.CHAPTER_READ, label: 'View chapters' },
      { value: UserPermissions.CHAPTER_CREATE, label: 'Create chapters' },
      { value: UserPermissions.CHAPTER_UPDATE, label: 'Edit chapters' },
      { value: UserPermissions.CHAPTER_DELETE, label: 'Delete chapters' },
    ],
  },
  {
    label: 'Pages',
    permissions: [
      { value: UserPermissions.PAGE_READ, label: 'View pages' },
      { value: UserPermissions.PAGE_CREATE, label: 'Create pages' },
      { value: UserPermissions.PAGE_UPDATE, label: 'Edit pages' },
      { value: UserPermissions.PAGE_DELETE, label: 'Delete pages' },
    ],
  },
  {
    label: 'Layouts',
    scope: 'global',
    permissions: [
      { value: UserPermissions.LAYOUT_READ, label: 'View layouts' },
      { value: UserPermissions.LAYOUT_CREATE, label: 'Create layouts' },
      { value: UserPermissions.LAYOUT_UPDATE, label: 'Edit layouts' },
      { value: UserPermissions.LAYOUT_DELETE, label: 'Delete layouts' },
    ],
  },
  {
    label: 'Translations',
    scope: 'global',
    permissions: [
      { value: UserPermissions.TRANSLATION_READ, label: 'View translations' },
      {
        value: UserPermissions.TRANSLATION_CREATE,
        label: 'Create translations',
      },
      { value: UserPermissions.TRANSLATION_UPDATE, label: 'Edit translations' },
      {
        value: UserPermissions.TRANSLATION_DELETE,
        label: 'Delete translations',
      },
    ],
  },
];

// The scope of one option, resolved against its group. Both defaults live here
// rather than at each call site, so "unmarked means book-scoped" is stated once.
export function bookPermissionScopeOf(
  group: IBookPermissionGroup,
  option: IBookPermissionOption,
): BookPermissionScope {
  return option.scope ?? group.scope ?? 'book';
}

// Every permission the dialog shows but cannot grant, whatever the reason.
//
// The write path filters against this rather than against a per-reason list:
// nothing downstream of the checkbox cares *why* a permission is ungrantable,
// only that it must never be stored.
export const NON_GRANTABLE_BOOK_PERMISSIONS: ReadonlySet<string> = new Set(
  BOOK_PERMISSION_GROUPS.flatMap((group) =>
    group.permissions
      .filter((option) => bookPermissionScopeOf(group, option) !== 'book')
      .map((option) => option.value),
  ),
);

// Permissions that subsume the granular ones for the same object. A member who
// holds chapter.full already has every chapter permission, so granting them
// individually would be noise.
export const BOOK_PERMISSION_SUPERSETS: Record<string, string[]> = {
  [UserPermissions.CHAPTER_FULL]: [
    UserPermissions.CHAPTER_READ,
    UserPermissions.CHAPTER_CREATE,
    UserPermissions.CHAPTER_UPDATE,
    UserPermissions.CHAPTER_DELETE,
  ],
  [UserPermissions.PAGE_FULL]: [
    UserPermissions.PAGE_READ,
    UserPermissions.PAGE_CREATE,
    UserPermissions.PAGE_UPDATE,
    UserPermissions.PAGE_DELETE,
  ],
};

// A book's full access list.
export interface IBookAccessDetail {
  BookId: string;

  // True when the book carries any grant at all - including the marker grant
  // the server writes for an admins-only book. That marker is filtered out of
  // `Roles`, so on an admins-only book every list below is empty and this flag
  // is the only thing distinguishing it from a book visible to everyone.
  IsRestricted: boolean;

  Roles: IBookRoleAccess[];
  Members: IBookMemberAccess[];

  // When the book's access was last changed - the newest grant timestamp, not
  // the book's own UpdatedAt. Null when the book carries no grant, since a
  // cleared book keeps no record of when it was cleared.
  AccessUpdatedAt: string | null;

  // Who made that change. An id, not a name: this screen already loads the
  // full member list, so it resolves the name itself rather than making the
  // access endpoint join users on every book in the catalogue.
  //
  // Null both when the book has never been changed and when the grant predates
  // this column, which is why the tooltip has to survive not finding anyone.
  AccessUpdatedBy: string | null;
}

// A member entry in a save request.
//
// Permissions are the grant: they say what this member may do on this book,
// independently of the roles they hold elsewhere. An empty list means the
// member reaches the book but gains nothing beyond what their own roles
// already give them.
export interface ISetBookMemberAccess {
  UserId: string;
  Permissions: string[];
}

// Replace-set semantics: whatever is sent becomes the book's entire access
// list, so omitting an existing grant removes it.
export interface ISetBookAccessRequest {
  // False means "visible to everyone", and the server rejects the request if
  // any grant accompanies it. True with both lists empty is the staff-only
  // state, which the server records with a marker grant of its own choosing.
  IsRestricted: boolean;
  RoleIds: number[];
  Members: ISetBookMemberAccess[];
}

// Role classification. Book access is granted using User-type roles today;
// subscription tiers are filtered out of the picker.
export enum RoleType {
  User = 1,
  Book = 2,
}

// Subscription tier roles are not meaningful as book-access roles and are
// excluded from the assignment dropdown.
export const SUBSCRIBER_ROLE_NAMES: string[] = [
  'FreeUser',
  'StandardUser',
  'PremiumUser',
];

export interface ILayout {
  Name: string;
  Cols: number;
  Rows: number;
  Data: string;
  isGlobal?: boolean; // need to add it as the global lay have this property but the page layout does not have it
  LayoutId?: string; // this options as the pageLayout does not have access to its global layout id
  Sections: ISection[];
  CreatedAt?: string;
  UpdatedAt?: string;
}
export interface ILayoutList {
  LayoutId: string;
  Name: string;
  Cols: number;
  Rows: number;
  IsGlobal?: boolean;
  CreatedAt?: string;
  UpdatedAt?: string;
}
export interface ISection {
  Data: string;
  AudioUrl: string;
}
export interface IPageResponse {
  value: {
    chapterId: string;
    groupName: string;
    type: number;
    title: string;
    description: string;
    status: number;
    accessRights: string[];
    webLayout: ILayout;
    mobileLayout: ILayout;
    tabletLayout: ILayout;
    audioFileUrl: string;
    thumbnailUrl: string;
    createdAt: string;
    updatedAt: string;
  }[];

  errors: {
    code: string;
    message: string;
    stackTrace: string;
    innerErrors: (string | null)[];
  }[];
}

// create book request
export interface ICreateBookRequest {
  Title: string | null;
  Description: string | null;
  DefaultLanguage: string | null;
  ThumbnailUrl: string | null;
  AudioUrl: string | null;
  AuthorName: string | null;
  AuthorDOB: string | null;
  Chapters: ICreateChapterRequest[];
}

export interface ICreateChapterRequest {
  GroupName: string | null;
  Title: string | null;
  Type: ChapterType;
  Description: string | null;
  AccessRights?: string[];
  ThumbnailUrl: string | null;
  IconUrl: string | null;
  AudioUrl: string | null;
  SubChapters: ICreateChapterRequest[];
}

export interface ICreateLayoutRequest {
  Name: string;
  Cols: number;
  Rows: number;
  Sections: ICreateSectionRequest[];
}
export interface ICreatePageRequest {
  Type: PageType;
  Layout: ICreateLayoutRequest;
  AccessRights: string[];
}

export interface ICreateSectionRequest {
  Data: string;
  AudioUrl: string;
}

export interface IDeleteSubChapterResponse {
  Value: string;
}

export interface IReportInfo {
  OrderId: string;
  Name?: string;
  UserId: string;
  User?: IUserInfo;
  Amount: number;
  CGST: number;
  SGST: number;
  IGST: number;
  OtherTax: number;
  TotalAmount: number;
  CreatedAt: string;
  UpdateAt: string;
  BillingAddress: IAddressInfo;
  PurchaseDate?: string;
  InvoiceNo?: string;
  BillingAddressId?: string;
}

export interface IReportInfoResponse {
  Value: IReportInfo[];
}

export interface IFetchUserReportsRequest {
  PageNumber: number;
  PageSize: number;
  SubscriptionTypes?: SubscriptionType[];
  Search?: string | null;
  RegistrationDateFrom?: string | null;
  RegistrationDateTo?: string | null;
}

export interface IUserReportInfo {
  UserID: string;
  FullName: string;
  Email: string;
  MobileNumber: string;
  PictureUrl: string;
  RegistrationDate: string;
  SubscriptionPurchaseDate: string | null;
  SubscriptionStartDate: string | null;
  SubscriptionEndDate: string | null;
  BooksUnlocked: number | null;
  FirstBookUnlockDate: string | null;
  SubscriptionType: SubscriptionType | null;
  UserRoles: string[];
}

export interface IUserReportPagedResult {
  PageNumber: number;
  PageSize: number;
  RecordsInPage: number;
  TotalPages: number;
  TotalCount: number;
  Value: IUserReportInfo[];
  Errors: IErrors[];
}

// ---------------------------------------------------------------------------
// Role, permission and staff management
// ---------------------------------------------------------------------------

/** Matches Role.RoleName in the database, and the server's own validation. */
export const MAX_ROLE_NAME_LENGTH = 20;

/**
 * Matches RoleConstants.MinRoleNameLength.
 *
 * A role name is what somebody picks off a list months later. One character
 * cannot tell two roles apart, and is nearly always a slip.
 */
export const MIN_ROLE_NAME_LENGTH = 2;

/**
 * Matches RoleConstants.RoleNamePattern on the server, kept in step by hand
 * because the two repositories share no contract. The server's copy decides;
 * this exists so the person typing finds out before pressing Save.
 *
 * Letters, digits, single spaces, and the three marks that turn up in real
 * titles - hyphen for "Sub-Editor", apostrophe, ampersand for "Sales & Support".
 * Must start and end with a letter or digit, so a name cannot be padded into
 * looking distinct.
 */
export const ROLE_NAME_PATTERN =
  /^[\p{L}\p{N}](?:[\p{L}\p{N} '&\-]*[\p{L}\p{N}])?$/u;

/**
 * The roles that subscriptions assign automatically, by name.
 *
 * Only decides which of the two "cannot be edited" sentences to show. The
 * server refuses these regardless and sends its own wording back; this exists
 * so the tooltip on a row can say why before anybody presses anything.
 *
 * By name rather than by id, because the ids are no longer a band the client
 * can reason about - roles created here take the next consecutive number.
 */
export const SUBSCRIPTION_ASSIGNED_ROLES = [
  'FreeUser',
  'StandardUser',
  'PremiumUser',
];

/**
 * Roles that cannot be renamed, disabled, or have their permissions changed.
 *
 * The server decides this and sends IsProtected, and that answer wins wherever
 * it is present. This list exists because "wherever it is present" is not
 * everywhere: an older build of the API omits the field entirely, and an absent
 * boolean reads as false - which unlocks Admin on the screen rather than
 * locking it. Failing closed matters more here than staying in one place, so
 * the names are duplicated deliberately.
 *
 * Kept in step with RoleConstants.ProtectedRoleIds on the server.
 */
export const PROTECTED_ROLE_NAMES = ['Admin', ...SUBSCRIPTION_ASSIGNED_ROLES];

/**
 * Roles that cannot be granted or removed from the Manage User Roles screen.
 *
 * A different question from PROTECTED_ROLE_NAMES, which is about editing the
 * role itself. These are about *membership*, and the two lists happen to hold
 * the same names for different reasons:
 *
 * - Admin because granting it from a screen would let one compromised
 *   administrator session mint more administrators, while removal is already
 *   impossible from here. Membership is set in the database instead.
 * - The subscriber tiers because a subscription assigns them; one granted by
 *   hand is backed by nothing, and one removed by hand comes back on the next
 *   subscription event anyway.
 *
 * Mirrors RoleService.GuardRoleIsAssignable. The server refuses these whatever
 * the client does - this exists so the picker can say why rather than offering
 * a choice that fails on save.
 */
export const UNASSIGNABLE_ROLE_NAMES = [
  'Admin',
  ...SUBSCRIPTION_ASSIGNED_ROLES,
];

/** Whether a role may be granted or removed from the user role screen. */
export const isRoleAssignable = (
  role: { RoleName?: string } | null | undefined,
): boolean => !UNASSIGNABLE_ROLE_NAMES.includes((role?.RoleName ?? '').trim());

/** Whether a role is unassignable specifically because subscriptions own it. */
export const isSubscriptionAssignedRole = (
  role: { RoleName?: string } | null | undefined,
): boolean =>
  SUBSCRIPTION_ASSIGNED_ROLES.includes((role?.RoleName ?? '').trim());

/** Whether a role is read-only, preferring the server's answer over the list. */
export const isRoleProtected = (
  role: { RoleName?: string; IsProtected?: boolean } | null | undefined,
): boolean =>
  role?.IsProtected === true ||
  PROTECTED_ROLE_NAMES.includes((role?.RoleName ?? '').trim());

/**
 * The two permissions a customer-facing role needs before somebody holding it
 * can even open their own account page. Suggested rather than forced: an
 * internal role legitimately needs neither.
 */
export const ACCOUNT_BASELINE_PERMISSIONS = ['profile.read', 'profile.update'];

/** Matches Permission.Description. */
export const MAX_PERMISSION_DESCRIPTION_LENGTH = 200;

/**
 * Matches RoleConstants.MinPermissionDescriptionLength.
 *
 * A description exists to explain what granting a permission does, and one or
 * two characters cannot. Clearing the field entirely stays allowed - that is
 * how a description is removed on purpose.
 */
export const MIN_PERMISSION_DESCRIPTION_LENGTH = 3;

/**
 * Matches RoleConstants.PermissionDescriptionPattern on the server.
 *
 * Kept in step by hand, because there is no shared contract between the two
 * repositories. The server's copy is the one that decides; this exists so the
 * person typing finds out before they press Save rather than after.
 *
 * Letters, digits, whitespace and ordinary sentence punctuation. Angle
 * brackets, braces, pipes, backslashes and backticks are left out: none of
 * them belong in prose, and all of them are the shape of something being
 * smuggled through a text field.
 */
export const PERMISSION_DESCRIPTION_PATTERN =
  /^[\p{L}\p{N}\p{Zs}.,;:'"\-‐-―‘’“”()[\]/!?&%@#+=_*]+$/u;

/**
 * A role as shown on the Manage Roles tab.
 *
 * Wider than IRoleDetail, which fills the book-access picker and deliberately
 * hides disabled roles. This one includes them, because a disabled role is
 * exactly what an administrator needs to see in order to re-enable it.
 */
export interface IManagedRole {
  RoleId: number;
  RoleName: string;
  RoleType: number;
  IsDisabled: boolean;

  /**
   * Sent by the server rather than derived from the id here, so that the two
   * can never drift. The client knows the rule as well, but only to explain
   * itself - the server's answer is the one that decides.
   */
  IsProtected: boolean;

  /** Distinct users holding the role. Shown in the members column. */
  MemberCount: number;

  Permissions: string[];
}

/** A permission as shown on the Manage Permissions tab. */
export interface IPermissionDetail {
  PermissionId: number;
  PermissionName: string;
  Description: string | null;
  IsDisabled: boolean;

  /**
   * Whether any code actually enforces this permission.
   *
   * A row can outlive the attribute that read it. Granting one of those looks
   * like it worked and confers nothing, so the screen marks it rather than
   * hiding it - hiding would leave a row nobody can find to delete.
   */
  IsRecognised: boolean;

  /** Roles currently holding it. */
  RoleCount: number;
}

export interface ICreateRoleRequest {
  RoleName: string;
  Permissions: string[];
}

/**
 * IsDisabled is optional rather than defaulted, so "leave it alone" and "set it
 * to false" stay different requests. The same is true of RoleName.
 */
export interface IUpdateRoleRequest {
  RoleId: number;
  RoleName?: string;
  IsDisabled?: boolean;
}

/** The whole set, not a delta - what the screen shows is what gets stored. */
export interface ISetRolePermissionsRequest {
  RoleId: number;
  Permissions: string[];
}

/**
 * PermissionName is deliberately absent. It is the join between the row and the
 * attributes in the code, so renaming it would not rename anything - it would
 * detach the row and silently revoke the permission everywhere.
 */
export interface IUpdatePermissionRequest {
  PermissionId: number;
  Description?: string;
  IsDisabled?: boolean;
}

export interface IAssignUserRoleRequest {
  UserId: string;
  RoleId: number;
}

export interface IRevokeUserRoleRequest {
  UserId: string;
  RoleId: number;
}

/**
 * What actually happened to the user's subscription alongside the role change.
 *
 * Reported rather than assumed, because the three outcomes are materially
 * different to the person being changed - one of them bills them nothing and
 * one of them may have handed back a paid subscription they still had running -
 * and the screen should be able to say which.
 */
export interface IUserRoleChangeResponse {
  UserId: string;
  RoleId: number;
  RoleName: string;
  PrimarySubscription: string | null;

  /** A free subscription was issued because nothing was left to reuse. */
  CreatedSubscription: boolean;

  /** An existing subscription was pointed at again. */
  RestoredSubscription: boolean;

  /** The primary subscription pointer was cleared by an assignment. */
  ClearedSubscription: boolean;

  /**
   * When the role was granted. Null on a revoke.
   *
   * Carried so a newly added chip can be dated immediately, rather than
   * showing blank until the next full load of the member list.
   */
  GrantedAt?: string | null;
}

/**
 * Whether a role makes its holder staff rather than a subscriber.
 *
 * Mirrors RoleConstants.IsStaffRole. Expressed as an exclusion for the same
 * reason: a role invented at runtime is staff, because nothing issues
 * subscriptions against one.
 */
export const SUBSCRIBER_ROLE_IDS: number[] = [3, 4, 5];

export function isStaffRoleId(roleId: number): boolean {
  return !SUBSCRIBER_ROLE_IDS.includes(roleId);
}
